import { HttpResponse, http, type HttpHandler } from 'msw';
import { BFF_ENDPOINTS } from '../../api/endpoints/BffEndpoints';
import { createMockPaymentResponse, createMockPaymentIntentResponse, createMockPayerResponse, createMockPaymentAuthenticationResponse } from '../factories/payment';
import { createMockInvoice } from '../factories/invoice';
import { toMSWPattern } from '../utils/generic';
import type { HTTP_METHODS } from './constants/generic';
import { HTTP_METHODS_VALUES } from './constants/generic';

interface HandlerMapping {
  pattern: string;
  key: string;
  method?: HTTP_METHODS;
}

export interface HandlerWithMapping {
  handler: HttpHandler;
  mapping: HandlerMapping;
}

export const ENDPOINTS_KEYS = {
  PAYMENT_BY_PROVIDER: 'PAYMENT_BY_PROVIDER',
  SUBSCRIPTIONS_PAYMENT_INTENT: 'SUBSCRIPTIONS_PAYMENT_INTENT',
  PAYERS: 'PAYERS',
  INVOICES: 'INVOICES',
  PAYMENT_AUTHENTICATIONS: 'PAYMENT_AUTHENTICATIONS',
  PAYMENT_AUTHENTICATION: 'PAYMENT_AUTHENTICATION',
};

// Device data "lands" on this GET of an authentication, like a real DDC iframe would.
const DDC_COLLECTED_ON_READ = 2;

export function createPaymentHandlers(baseUrl: string): HandlerWithMapping[] {
  // Authentication id -> reads so far. Per handler set, so every setup starts from clean state.
  const authenticationReads = new Map<string, number>();

  const handlers: HandlerWithMapping[] = [
    {
      handler: http.get(`${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.PAYMENT_BY_PROVIDER)}`, ({ params }) => {
        const { paymentId } = params;
        const payment = createMockPaymentResponse({
          id: paymentId as string,
        });
        return HttpResponse.json(payment);
      }),
      mapping: {
        pattern: 'payment',
        key: ENDPOINTS_KEYS.PAYMENT_BY_PROVIDER,
        method: HTTP_METHODS_VALUES.GET as HTTP_METHODS,
      },
    },
    {
      handler: http.post(`${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.SUBSCRIPTIONS_PAYMENT_INTENT)}`, () => {
        const paymentIntent = createMockPaymentIntentResponse();
        return HttpResponse.json(paymentIntent);
      }),
      mapping: {
        pattern: 'payment-intent',
        key: ENDPOINTS_KEYS.SUBSCRIPTIONS_PAYMENT_INTENT,
        method: HTTP_METHODS_VALUES.POST as HTTP_METHODS,
      },
    },
    {
      handler: http.post(`${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.PAYERS)}`, () => {
        const payer = createMockPayerResponse();
        return HttpResponse.json(payer);
      }),
      mapping: {
        pattern: 'payer',
        key: ENDPOINTS_KEYS.PAYERS,
        method: HTTP_METHODS_VALUES.POST as HTTP_METHODS,
      },
    },
    {
      handler: http.get(`${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.INVOICES)}`, () => {
        return HttpResponse.json([createMockInvoice()]);
      }),
      mapping: {
        pattern: 'invoices',
        key: ENDPOINTS_KEYS.INVOICES,
        method: HTTP_METHODS_VALUES.GET as HTTP_METHODS,
      },
    },
    {
      handler: http.post(`${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.PAYMENT_AUTHENTICATIONS)}`, () => {
        const authentication = createMockPaymentAuthenticationResponse();
        return HttpResponse.json(authentication, { status: 201 });
      }),
      mapping: {
        pattern: 'payments/authentications',
        key: ENDPOINTS_KEYS.PAYMENT_AUTHENTICATIONS,
        method: HTTP_METHODS_VALUES.POST as HTTP_METHODS,
      },
    },
    {
      handler: http.get(`${baseUrl}/${toMSWPattern(BFF_ENDPOINTS.PAYMENT_AUTHENTICATION)}`, ({ params }) => {
        const authenticationId = params.authenticationId as string;
        const reads = (authenticationReads.get(authenticationId) ?? 0) + 1;
        authenticationReads.set(authenticationId, reads);

        const authentication = createMockPaymentAuthenticationResponse({
          id: authenticationId,
          providerData: { ddcIframeUrl: 'https://ddc.mock.invalid/collect', ddcCollected: reads >= DDC_COLLECTED_ON_READ, ddcExpired: false },
        });
        return HttpResponse.json(authentication);
      }),
      mapping: {
        pattern: 'payments/authentications/:authenticationId',
        key: ENDPOINTS_KEYS.PAYMENT_AUTHENTICATION,
        method: HTTP_METHODS_VALUES.GET as HTTP_METHODS,
      },
    },
  ];
  return handlers;
}
